/**
 * CompanySwitcher — F9 T9.1 (slice 1D) + tryb "Wszystkie firmy" (all-companies).
 *
 * Dropdown w `TenantTopbar` pokazujacy aktywna firme + liste dostepnych firm.
 * Klik na firme → setStoredActiveCompanyId + invalidate `/me` (backend zwroci
 * activeCompanyId zgodnie z nowym X-Company-Id headerem). Gdy user ma >= 2 firmy,
 * na gorze listy jest dodatkowa opcja "Wszystkie" — wybor OMIJA X-Company-Id na
 * requestach (patrz `lib/api.ts` interceptor), backend zwraca wtedy unie danych
 * ze wszystkich firm usera.
 *
 * Edge cases:
 *  - 0 firm → null (nie renderujemy nic)
 *  - 1 firma → wyswietlamy tylko nazwe bez dropdownu (opcja "Wszystkie" niedostepna)
 *  - localStorage z nieosiagalnym id → fallback chain (active-company.ts)
 *  - localStorage === sentinel "Wszystkie" ale firmy skurczyly sie do < 2 → sentinel
 *    ignorowany, auto-collapse do jedynej firmy (patrz resolveActiveCompanyId)
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Layers, Star } from 'lucide-react';

import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { ALL_COMPANIES, setStoredActiveCompanyId } from '@/lib/active-company';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useAuthedImage } from '@/lib/use-authed-image';

export function CompanySwitcher() {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  // Panel listy renderowany PORTALEM do body (position: fixed) — przełącznik żyje teraz
  // w sidebarze, który przy zwinięciu jest wąski i ma `overflow`, więc `absolute` panel
  // był przycinany/uciekał poza ekran (reguła §4.12). Fixed + portal + pozycja z triggera.
  const panelRef = useRef<HTMLUListElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);

  const scope = useActiveCompanyScope();
  const { isAll } = scope;

  const activeCompany = useMemo(
    () => companies.find((c) => c.id === scope.companyId) ?? null,
    [companies, scope.companyId],
  );

  // Pozycja portalowanego panelu — z prostokąta triggera; kotwiczymy LEWĄ krawędź panelu
  // do lewej triggera (nie prawej — w wąskim sidebarze panel 288px z prawej uciekał w lewo
  // poza ekran) i przycinamy do viewportu. Odświeżamy przy scroll/resize (panel `fixed`).
  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const PANEL_WIDTH = 288; // w-72
    const update = () => {
      const el = dropdownRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.left, window.innerWidth - PANEL_WIDTH - 8));
      setMenuPos({ top: r.bottom + 4, left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // Click-outside zamyka dropdown. Panel jest w PORTALU (poza dropdownRef), więc klik w
  // opcję liczyłby się jako „na zewnątrz" — dlatego wykluczamy też panelRef.
  useEffect(() => {
    if (!open) return;
    const handler = (ev: MouseEvent) => {
      const target = ev.target as Node;
      const inTrigger = dropdownRef.current?.contains(target) ?? false;
      const inPanel = panelRef.current?.contains(target) ?? false;
      if (!inTrigger && !inPanel) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!me || companies.length === 0) return null;

  if (companies.length === 1) {
    const only = companies[0]!;
    return (
      <div
        className="flex h-11 w-full items-center gap-2.5 rounded-[28px] bg-[#f1f1f4] px-2.5 dark:bg-bg-secondary"
        data-testid="company-switcher-single"
      >
        <CompanyAvatar company={only} />
        <span className="max-w-[180px] truncate text-[14px] font-medium text-text-primary">
          {only.name}
        </span>
      </div>
    );
  }

  function handleSelect(companyId: string) {
    const alreadySelected =
      companyId === ALL_COMPANIES ? isAll : !isAll && companyId === scope.companyId;
    if (alreadySelected) {
      setOpen(false);
      return;
    }
    setStoredActiveCompanyId(companyId);
    // Zmiana aktywnej firmy zmienia widoczność danych we WSZYSTKICH kartotekach
    // (faktury, umowy, pisma, dokumenty, dashboard, procesy — nie tylko
    // kontrahenci/pracownicy). Karty są trzymane keep-alive (TenantTabPanels) +
    // staleTime 60s, więc ręczna lista unieważnień była niedomykalna: aktywne
    // zapytania zostawały ze stanem poprzedniej firmy. Globalny invalidate jest
    // jedynym pewnym wariantem (aktywne odświeżają się od razu, nieaktywne przy
    // ponownym wejściu na kartę).
    queryClient.invalidateQueries();
    setOpen(false);
  }

  return (
    <div className="relative w-full" ref={dropdownRef} data-testid="company-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-11 w-full items-center gap-2.5 rounded-[28px] bg-[#f1f1f4] px-2.5 transition-colors hover:bg-[#e8e8ee] dark:bg-bg-secondary"
        data-testid="company-switcher-toggle"
      >
        {isAll ? (
          <div className="flex h-6 w-6 items-center justify-center rounded bg-bg-secondary text-text-muted">
            <Layers size={14} aria-hidden="true" />
          </div>
        ) : activeCompany ? (
          <CompanyAvatar company={activeCompany} />
        ) : null}
        <span className="text-sm font-medium text-text-primary truncate max-w-[180px]">
          {isAll
            ? t('tenant.companySwitcher.allCompanies')
            : (activeCompany?.name ?? t('tenant.companySwitcher.selectPlaceholder'))}
        </span>
        {!isAll && activeCompany?.isPrimary && (
          <Star
            size={12}
            className="text-amber-500"
            aria-label={t('tenant.companies.badge.primary')}
          />
        )}
        <ChevronDown size={14} aria-hidden="true" />
      </button>

      {open &&
        menuPos &&
        createPortal(
          <ul
            ref={panelRef}
            role="listbox"
            aria-label={t('tenant.companySwitcher.listLabel')}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: 288,
              zIndex: 60,
            }}
            className="max-h-80 overflow-y-auto rounded-lg border border-border-secondary bg-bg-elevated shadow-lg"
            data-testid="company-switcher-list"
          >
            <li role="option" aria-selected={isAll}>
              <button
                type="button"
                onClick={() => handleSelect(ALL_COMPANIES)}
                aria-label={t('tenant.companySwitcher.allCompaniesAria')}
                className={[
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-secondary',
                  isAll ? 'bg-bg-secondary' : '',
                ].join(' ')}
                data-testid="company-switcher-all"
              >
                <div className="flex h-6 w-6 items-center justify-center rounded bg-bg-secondary text-text-muted">
                  <Layers size={14} aria-hidden="true" />
                </div>
                <span className="flex-1 truncate text-text-primary">
                  {t('tenant.companySwitcher.allCompanies')}
                </span>
                {isAll && <Check size={14} className="text-accent-600" aria-hidden="true" />}
              </button>
            </li>
            {companies.map((c) => {
              const selected = !isAll && c.id === scope.companyId;
              return (
                <li key={c.id} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => handleSelect(c.id)}
                    className={[
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-secondary',
                      selected ? 'bg-bg-secondary' : '',
                    ].join(' ')}
                    data-testid={`company-switcher-item-${c.id}`}
                  >
                    <CompanyAvatar company={c} />
                    <span className="flex-1 truncate text-text-primary">{c.name}</span>
                    {c.isPrimary && (
                      <Star
                        size={12}
                        className="text-amber-500"
                        aria-label={t('tenant.companies.badge.primary')}
                      />
                    )}
                    {selected && <Check size={14} className="text-accent-600" aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </div>
  );
}

function CompanyAvatar({ company }: { company: { name: string; logoUrl: string | null } }) {
  // `logoUrl` = ścieżka trasy API (wymaga tokenu) — obrazek przez blob, nie bezpośrednio w <img>.
  const logoSrc = useAuthedImage(company.logoUrl);
  if (logoSrc) {
    return <img src={logoSrc} alt="" className="h-6 w-6 rounded-md object-cover" />;
  }
  // Inicjały firmy (do 2 liter) w jasnoniebieskim kwadraciku — jak na makiecie „TE Test Eugenia".
  const initials =
    company.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || '?';
  return (
    <div className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[9px] bg-accent-100 text-[12px] font-medium text-accent-700 dark:bg-accent-900/40 dark:text-accent-300">
      {initials}
    </div>
  );
}
